/**
 * localStorage-persisted debug logger for diagnosing Android 16 LINE login failures.
 *
 * Activated when:
 * - Environment variable VITE_DEBUG_MODE is set to 'true' (for staging builds)
 * - URL contains `?debug` parameter (only in non-production environments)
 *
 * Security note: The ?debug URL parameter is disabled in production builds
 * to prevent unintended information disclosure. Use VITE_DEBUG_MODE for
 * controlled staging/development debugging.
 *
 * In normal operation every public function short-circuits immediately,
 * so there is zero runtime overhead in production.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "flaro_debug_logs";
const MAX_ENTRIES = 200;
const MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DebugLogEntry {
  timestamp: string;
  page: string;
  event: string;
  data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Debug-mode detection (cached)
// ---------------------------------------------------------------------------

let debugModeCache: boolean | null = null;

export function isDebugMode(): boolean {
  if (debugModeCache !== null) {
    return debugModeCache;
  }
  // Enable debug mode via:
  // 1. Environment variable VITE_DEBUG_MODE=true (for staging/dev builds)
  // 2. URL parameter ?debug (non-production environments only)
  // 3. liff.state エンコード済みパラメータ内の debug（LIFF経由アクセス時）
  const hasEnvFlag = import.meta.env.VITE_DEBUG_MODE === "true";
  const params = new URLSearchParams(window.location.search);
  const hasUrlParam = params.has("debug");

  // liff.state 内の debug パラメータもチェック
  // LIFF経由ではクエリパラメータが liff.state にエンコードされるため
  let hasLiffStateParam = false;
  const liffState = params.get("liff.state");
  if (liffState) {
    try {
      const decoded = decodeURIComponent(liffState);
      const queryPart = decoded.includes("?") ? decoded.split("?")[1] : decoded;
      hasLiffStateParam = new URLSearchParams(queryPart).has("debug");
    } catch {
      // パース失敗は無視
    }
  }

  const isProduction = import.meta.env.PROD;
  debugModeCache = hasEnvFlag || (!isProduction && (hasUrlParam || hasLiffStateParam));
  return debugModeCache;
}

// ---------------------------------------------------------------------------
// Core logging
// ---------------------------------------------------------------------------

export function addLog(
  page: string,
  event: string,
  data?: Record<string, unknown>,
): void {
  if (!isDebugMode()) {
    return;
  }

  const entry: DebugLogEntry = {
    timestamp: new Date().toISOString(),
    page,
    event,
    ...(data !== undefined && { data }),
  };

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    let logs: DebugLogEntry[];
    try {
      logs = raw ? (JSON.parse(raw) as DebugLogEntry[]) : [];
    } catch {
      // Corrupted data -- reset and start fresh.
      logs = [];
    }

    logs.push(entry);

    // Keep only the newest entries when the limit is exceeded.
    const trimmed = logs.length > MAX_ENTRIES ? logs.slice(-MAX_ENTRIES) : logs;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage may be full or unavailable -- best-effort only.
  }

  // Mirror to the browser console for real-time observation.
  console.log("[FLARO_DEBUG]", page, event, data ?? "");
}

// ---------------------------------------------------------------------------
// Retrieval
// ---------------------------------------------------------------------------

export function getLogs(): DebugLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const logs = JSON.parse(raw) as DebugLogEntry[];
    const cutoff = Date.now() - MAX_AGE_MS;

    return logs
      .filter((entry) => new Date(entry.timestamp).getTime() >= cutoff)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
  } catch {
    // Corrupted data -- clear to prevent persistent failure.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore -- localStorage may be unavailable.
    }
    return [];
  }
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function clearLogs(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore -- localStorage may be unavailable.
  }
}

// ---------------------------------------------------------------------------
// Platform diagnostics
// ---------------------------------------------------------------------------

export function getPlatformInfo(): Record<string, unknown> {
  try {
    const info: Record<string, unknown> = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: screen.width,
      screenHeight: screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };

    // UA Client Hints (Chromium-based browsers only).
    const nav = navigator as unknown as Record<string, unknown>;
    if (nav.userAgentData) {
      const uaData = nav.userAgentData as Record<string, unknown>;
      info.uaClientHints = {
        mobile: uaData.mobile,
        platform: uaData.platform,
        brands: uaData.brands,
      };
    }

    return info;
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Clipboard export
// ---------------------------------------------------------------------------

export async function copyLogsToClipboard(): Promise<boolean> {
  try {
    const payload = {
      logs: getLogs(),
      platformInfo: getPlatformInfo(),
      exportedAt: new Date().toISOString(),
    };

    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    return true;
  } catch (error) {
    console.warn("[FLARO_DEBUG] Failed to copy logs to clipboard:", error);
    return false;
  }
}
